import React, { useCallback, useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, ShieldAlert, Users, Layers } from "lucide-react";
import { getBatches, updateBatchStatus } from "../../api/fees/feeApi";
import {
  Button,
  Alert,
  Skeleton,
  Badge,
} from "../../components/ui";
import { useToast } from "../../context/ToastContext";

/**
 * Owner/principal widget: lists DRAFT fee batches that need approval
 * (typically created by staff). Approve from here or open Fee Batches page.
 */
const FeeBatchApprovalNotification = ({ enabled = true }) => {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actingId, setActingId] = useState(null);
  const [msg, setMsg] = useState("");

  const toast = useToast();

  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const res = await getBatches({ status: "DRAFT" });
      const list = res.data || res || [];
      const needs = (Array.isArray(list) ? list : []).filter(
        (b) => b.needsOwnerApproval || b.createdByType === "Staff"
      );
      setPending(needs);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load pending batches");
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const approve = async (id) => {
    setActingId(id);
    setMsg("");
    try {
      await updateBatchStatus(id, { status: "APPROVED" });
      setMsg("Batch approved. Staff can now publish.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Approve failed");
    } finally {
      setActingId(null);
    }
  };

  const formattedItems = useMemo(() => {
    return pending.map((b) => ({
      ...b,
      id: b._id,
      amountFormatted: Number(b.totalAmountGenerated || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      studentCount: b.totalStudentsCount || 0,
    }));
  }, [pending]);

  // Visibility is gated ONLY on having actual pending data.
  // We intentionally do NOT factor `loading` into this check —
  // otherwise the card flashes visible during the initial fetch
  // (loading=true) and then disappears once the empty result
  // comes back. Staying hidden until there's confirmed data
  // means: nothing renders on mount, nothing renders while
  // fetching, and nothing renders if the result is empty.
  if (!enabled || pending.length === 0) {
    return null;
  }

  return (
    <div className="mb-6">
      <Alert
        variant="warning"
        className="overflow-hidden border border-amber-200/90 bg-gradient-to-r from-amber-50/90 via-white to-amber-50/30 p-4 sm:p-5 shadow-sm"
      >
        {/* Header Bar */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-b border-amber-200/60 pb-3">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700 ring-1 ring-amber-500/20">
              <ShieldAlert className="h-5 w-5" />
            </span>
            <div>
              <h3 className="text-sm font-extrabold tracking-tight text-amber-950">
                Fee Batch Approvals
              </h3>
              <p className="text-xs font-medium text-amber-800/90 mt-0.5">
                Staff-created batches waiting for approval before publication.
              </p>
            </div>
          </div>

          <Link to="/fees/batches" className="shrink-0">
            <Button
              variant="ghost"
              size="sm"
              rightIcon={<ArrowRight className="h-3.5 w-3.5" />}
              className="w-full sm:w-auto font-bold text-amber-900 hover:bg-amber-100/80 hover:text-amber-950 rounded-xl"
            >
              Open Batches
            </Button>
          </Link>
        </div>

        {/* Action Status Messages */}
        
        {msg && (
          <p className="mt-2.5 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 p-2 rounded-lg">
            {msg}
          </p>
        )}

        {/* Batch List
            Note: since the component returns null whenever
            pending.length === 0, this branch always has at least
            one item by the time it renders — so the "loading
            skeleton" branch that used to live here is no longer
            reachable and has been removed. During background
            polls (loading=true), the previously-loaded `pending`
            list stays on screen until the new result arrives,
            so there's no blink on refresh either. */}
        <div className="mt-3.5 space-y-2">
          {formattedItems.map((b) => (
            <div
              key={b.id}
              className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl bg-white dark:bg-neutral-900 p-3 sm:p-3.5 border border-slate-200/80 shadow-xs transition-all duration-200 hover:border-amber-300 hover:shadow-xs"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-bold text-slate-900 dark:text-neutral-100 text-xs sm:text-sm">
                  {b.batchName}
                </span>
                <Badge
                  variant="warning"
                  className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5"
                >
                  {b.month}/{b.year}
                </Badge>
                <div className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-neutral-400">
                  <span className="flex items-center gap-1 font-semibold text-slate-800 dark:text-neutral-100">
                    ₹{b.amountFormatted}
                  </span>
                  <span>·</span>
                  <span className="flex items-center gap-1">
                    <Users className="h-3 w-3 text-slate-400 dark:text-neutral-500 shrink-0" />
                    {b.studentCount} student{b.studentCount !== 1 ? "s" : ""}
                  </span>
                </div>
              </div>

              <Button
                type="button"
                variant="primary"
                size="xs"
                isLoading={actingId === b.id}
                onClick={() => approve(b.id)}
                leftIcon={<CheckCircle2 className="h-3.5 w-3.5" />}
                className="w-full sm:w-auto bg-amber-600 hover:bg-amber-700 text-white font-bold border-none shadow-xs rounded-lg shrink-0"
              >
                Approve
              </Button>
            </div>
          ))}
        </div>
      </Alert>
    </div>
  );
};

export default FeeBatchApprovalNotification;